/**
 * Exchange Rate Service
 * Uses exchangerate-api.com (free tier) with daily caching in localStorage.
 * Falls back to hardcoded approximate rates if the API is unavailable.
 * All rates are relative to OMR (Omani Rial).
 */

const CACHE_KEY = 'exchange_rates_cache';
const CACHE_DATE_KEY = 'exchange_rates_date';

// Fallback rates to OMR (approximate, used only when API is unavailable)
const FALLBACK_RATES_TO_OMR = {
  OMR: 1,
  USD: 0.385,   // 1 USD = 0.385 OMR
  EUR: 0.420,   // 1 EUR = 0.420 OMR
  GBP: 0.490,   // 1 GBP = 0.490 OMR
  AUD: 0.250,   // 1 AUD = 0.250 OMR
  CAD: 0.285,   // 1 CAD = 0.285 OMR
  CHF: 0.430,   // 1 CHF = 0.430 OMR
  JPY: 0.00260, // 1 JPY = 0.00260 OMR
  CNY: 0.053,   // 1 CNY = 0.053 OMR
  INR: 0.00462, // 1 INR = 0.00462 OMR
  SAR: 0.1027,  // 1 SAR = 0.1027 OMR
  AED: 0.1048,  // 1 AED = 0.1048 OMR
  KWD: 1.253,   // 1 KWD = 1.253 OMR
  QAR: 0.1057,  // 1 QAR = 0.1057 OMR
  BHD: 1.021,   // 1 BHD = 1.021 OMR
  SGD: 0.285,   // 1 SGD = 0.285 OMR
  HKD: 0.0493,  // 1 HKD = 0.0493 OMR
  NZD: 0.232,   // 1 NZD = 0.232 OMR
  SEK: 0.0370,  // 1 SEK = 0.0370 OMR
  NOK: 0.0360,  // 1 NOK = 0.0360 OMR
};

export const SUPPORTED_CURRENCIES = [
  { value: 'OMR', label: 'OMR – Omani Rial' },
  { value: 'USD', label: 'USD – US Dollar' },
  { value: 'EUR', label: 'EUR – Euro' },
  { value: 'GBP', label: 'GBP – British Pound' },
  { value: 'AUD', label: 'AUD – Australian Dollar' },
  { value: 'CAD', label: 'CAD – Canadian Dollar' },
  { value: 'CHF', label: 'CHF – Swiss Franc' },
  { value: 'JPY', label: 'JPY – Japanese Yen' },
  { value: 'CNY', label: 'CNY – Chinese Yuan' },
  { value: 'INR', label: 'INR – Indian Rupee' },
  { value: 'SAR', label: 'SAR – Saudi Riyal' },
  { value: 'AED', label: 'AED – UAE Dirham' },
  { value: 'KWD', label: 'KWD – Kuwaiti Dinar' },
  { value: 'QAR', label: 'QAR – Qatari Riyal' },
  { value: 'BHD', label: 'BHD – Bahraini Dinar' },
  { value: 'SGD', label: 'SGD – Singapore Dollar' },
  { value: 'HKD', label: 'HKD – Hong Kong Dollar' },
  { value: 'NZD', label: 'NZD – New Zealand Dollar' },
  { value: 'SEK', label: 'SEK – Swedish Krona' },
  { value: 'NOK', label: 'NOK – Norwegian Krone' },
];

const getTodayDateString = () => new Date()?.toISOString()?.split('T')?.[0];

/**
 * Fetch live exchange rates from exchangerate-api.com (free, no key required for base endpoint).
 * Rates are fetched once per day and cached in localStorage.
 * Returns an object: { rates: { USD: 0.385, ... }, date: '2026-05-04', source: 'live'|'cache'|'fallback' }
 */
export const getExchangeRates = async () => {
  const today = getTodayDateString();

  // Check localStorage cache
  try {
    const cachedDate = localStorage.getItem(CACHE_DATE_KEY);
    const cachedRates = localStorage.getItem(CACHE_KEY);
    if (cachedDate === today && cachedRates) {
      const parsed = JSON.parse(cachedRates);
      if (parsed && typeof parsed === 'object') {
        return { rates: parsed, date: today, source: 'cache' };
      }
    }
  } catch (_) {
    // ignore cache read errors
  }

  // Try live API: exchangerate-api.com free endpoint (OMR base)
  try {
    const response = await fetch('https://open.er-api.com/v6/latest/OMR', {
      signal: AbortSignal.timeout(5000),
    });
    if (response?.ok) {
      const json = await response?.json();
      if (json?.result === 'success' && json?.rates) {
        // json.rates are: 1 OMR = X foreign currency
        // We need: 1 foreign currency = Y OMR
        const ratesFromForeign = {};
        Object.entries(json?.rates)?.forEach(([currency, rateFromOMR]) => {
          if (rateFromOMR && rateFromOMR > 0) {
            ratesFromForeign[currency] = parseFloat((1 / rateFromOMR)?.toFixed(8));
          }
        });
        ratesFromForeign['OMR'] = 1;

        // Cache it
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(ratesFromForeign));
          localStorage.setItem(CACHE_DATE_KEY, today);
        } catch (_) {}

        return { rates: ratesFromForeign, date: today, source: 'live' };
      }
    }
  } catch (_) {
    // API unavailable, fall through to fallback
  }

  // Fallback to hardcoded approximate rates
  return { rates: FALLBACK_RATES_TO_OMR, date: today, source: 'fallback' };
};

/**
 * Get the OMR conversion rate for a specific currency.
 * Returns { rate, date, source }
 */
export const getRateToOMR = async (currency) => {
  if (!currency || currency === 'OMR') {
    return { rate: 1, date: getTodayDateString(), source: 'fixed' };
  }
  const { rates, date, source } = await getExchangeRates();
  const rate = rates?.[currency] ?? FALLBACK_RATES_TO_OMR?.[currency] ?? 1;
  return { rate, date, source };
};

/**
 * Convert an amount from a given currency to OMR.
 * Returns { omrAmount, rate, date, source }
 */
export const convertToOMR = async (amount, currency) => {
  const { rate, date, source } = await getRateToOMR(currency);
  const omrAmount = parseFloat((parseFloat(amount || 0) * rate)?.toFixed(6));
  return { omrAmount, rate, date, source };
};
